import { AdminUserListItem, AdminUserDetails, AdminActionResponse, UserSearchFilters } from '../types';

export interface AdminUsersResponse {
  users: AdminUserListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export const adminService = {
  /**
   * Get list of all users with search and filter capabilities
   */
  async getUsers(
    filters: UserSearchFilters = {}
  ): Promise<AdminUsersResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.subscriptionStatus && filters.subscriptionStatus !== 'all') {
      params.append('subscriptionStatus', filters.subscriptionStatus);
    }
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    
    const response = await fetch(`/api/admin/users?${params.toString()}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch users');
    }

    return await response.json();
  },

  /**
   * Get detailed information for a specific user
   */
  async getUserDetails(
    userId: number
  ): Promise<AdminUserDetails> {
    const response = await fetch(`/api/admin/users/${userId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch user details');
    }

    return await response.json();
  },

  /**
   * Grant free access to a user
   */
  async grantFreeAccess(
    userId: number,
    reason?: string
  ): Promise<AdminActionResponse> {
    const response = await fetch(`/api/admin/users/${userId}/grant-free-access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to grant free access');
    }

    return await response.json();
  },

  /**
   * Grant a three-month full-access trial to a user
   */
  async grantTrial(
    userId: number,
    reason?: string
  ): Promise<AdminActionResponse> {
    const response = await fetch(`/api/admin/users/${userId}/grant-trial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to grant trial');
    }

    return await response.json();
  },

  /**
   * Revoke free access from a user
   */
  async revokeFreeAccess(
    userId: number,
    reason?: string
  ): Promise<AdminActionResponse> {
    const response = await fetch(`/api/admin/users/${userId}/revoke-free-access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to revoke free access');
    }

    return await response.json();
  },

  /**
   * Toggle admin status for a user
   */
  async toggleAdminStatus(
    userId: number,
    reason?: string
  ): Promise<AdminActionResponse> {
    const response = await fetch(`/api/admin/users/${userId}/toggle-admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to toggle admin status');
    }

    return await response.json();
  },
};